import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, MessageBar, MessageBarBody, MessageBarTitle, Spinner, Tooltip } from "@fluentui/react-components";
import { Delete16Filled } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import IDeleteFolderProps from "./IDeleteFolderProps";
import folderService from "../../services/folderService";
import Status from "../../enums/Status";
import useStyleUtils from "../../utilities/styles";
import { useTranslation } from "react-i18next";
import { mergeStyles } from "@fluentui/merge-styles";
import { DeleteError, LoadError } from "./Errors";
import IFolderChildren from "../../models/folder/IFolderChildren";
import Prerequisites from "./DeletePrerequisites";

export default function DeleteFolder(props: IDeleteFolderProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [children, setChildren] = useState<IFolderChildren>(null!);

    const styles = useStyleUtils();

    const triggerStyles = mergeStyles({
        color: "red"
    });

    useEffect(() => {
        if (open) {
            setStatus(Status.LOADING);
            getFolderChildren(props.folderId)
                .then(children => {
                    setChildren(children);
                    setStatus(Status.LOAD_SUCCESS);
                })
                .catch(_ => setStatus(Status.LOAD_ERROR));
        }
    }, [open, props.folderId]);
    
    const handleSubmit = () => {
        setStatus(Status.DELETING);
        folderService.deleteFolder(props.folderId)
            .then(() => {
                props.onDelete();
                setOpen(false);
            })
          .catch(_ => setStatus(Status.DELETE_ERROR));
    };

    const isReadonly = [Status.DELETING, Status.DELETE_SUCCESS].includes(status);

    const isFolderEmpty = children && children.folders === 0 && children.passwords === 0 && children.personalPasswords.length === 0;
    
    return (
        <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={t("folders.titles.delete")} relationship='label'>
                    <Button icon={<Delete16Filled className={triggerStyles} />} />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <DialogTitle>{t("folders.titles.delete")}</DialogTitle>
                <DialogBody>
                    <DialogContent>
                        {
                            (status === Status.LOADING && <Spinner size="large" /> ) ||
                            (status === Status.LOAD_ERROR && <LoadError /> ) ||
                            (
                                status !== Status.DELETE_SUCCESS &&
                                (
                                    isFolderEmpty
                                        ? <p>{t("folders.delete.message")}</p>
                                        : <Prerequisites children={children} />
                                )
                            )
                        }
                        {
                            status === Status.DELETE_ERROR &&
                            <DeleteError />
                        }
                    </DialogContent>
                    {
                        (
                            (status !== Status.LOADING && status !== Status.LOAD_ERROR) &&
                            isFolderEmpty &&
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="primary" disabled={isReadonly}>
                                        {t("general.no")}
                                    </Button>
                                </DialogTrigger>
                                <Button appearance="secondary" onClick={handleSubmit} disabled={isReadonly}>
                                    {
                                        isReadonly && <Spinner size="tiny" className={styles.mr2} />
                                    }
                                    {t("general.yes")}
                                </Button>
                            </DialogActions>
                        ) ||
                        (
                            (status === Status.LOAD_ERROR || !isFolderEmpty) &&
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="primary">
                                        {t("general.close")}
                                    </Button>
                                </DialogTrigger>
                            </DialogActions>
                        )
                    }
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

async function getFolderChildren(folderId: string) : Promise<IFolderChildren> {
    const result = await folderService.countChildren(folderId);
    return result;
}